import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, getToken, setToken } from './api';
import type { Me, PublicConfig, SessionPayloadLike } from './session-types';

/**
 * Who is signed in, what their workspace may do, and how that state changes.
 *
 * `me` is refreshed rather than patched: licences, seats and the AI trial all
 * live on the server, and a stale copy is how a UI starts lying to people.
 */

type SessionState = {
  loading: boolean;
  me: Me | null;
  config: PublicConfig | null;
  error: string | null;
};

type SessionValue = SessionState & {
  signedIn: boolean;
  refresh: () => Promise<Me | null>;
  adopt: (payload: SessionPayloadLike) => Promise<Me | null>;
  signOut: () => Promise<void>;
  /** Days left on the AI trial, or null when this workspace is not on one. */
  trialDaysLeft: number | null;
  isDemo: boolean;
};

const SessionContext = createContext<SessionValue | null>(null);

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return null;
  return Math.max(0, Math.ceil((ts - Date.now()) / 86_400_000));
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ loading: true, me: null, config: null, error: null });

  const load = useCallback(async (): Promise<Me | null> => {
    try {
      const me = await api.me();
      setState((current) => ({ ...current, loading: false, me, error: null }));
      return me;
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      // 401/403 simply means "not signed in here" — not an error worth showing.
      setState((current) => ({
        ...current,
        loading: false,
        me: null,
        error: status === 401 || status === 403 ? null : (err as Error).message,
      }));
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const config = await api.config().catch(() => null);
      if (cancelled) return;
      setState((current) => ({ ...current, config }));

      // Local preview only: `?as=pm-1` signs in as that development identity.
      // The server accepts dev tokens exclusively when DEV_AUTH=1.
      if (config?.devAuth) {
        try {
          const as = new URLSearchParams(window.location.search).get('as');
          if (as) setToken(`dev:${as}`);
        } catch {
          /* ignore malformed query strings */
        }
      }

      // With a token, or anywhere inside the app, ask who is signed in — a
      // same-origin session cookie may still be valid. On the public pages we
      // stay quiet rather than firing a 401 at every visitor.
      const inApp = typeof window !== 'undefined'
        && /^\/(app|admin)/.test(window.location.pathname);
      if (getToken() || inApp) await load();
      else setState((current) => ({ ...current, loading: false }));
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const adopt = useCallback(
    async (payload: SessionPayloadLike) => {
      setToken(payload.token);
      return load();
    },
    [load],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* the local session is cleared regardless */
    }
    setToken('');
    setState((current) => ({ ...current, me: null }));
  }, []);

  const value = useMemo<SessionValue>(() => {
    const tenant = state.me?.tenant;
    return {
      ...state,
      signedIn: Boolean(state.me),
      refresh: load,
      adopt,
      signOut,
      trialDaysLeft: tenant?.aiTrial ? daysUntil(tenant.expiresAt) : null,
      isDemo: Boolean(tenant?.isDemo),
    };
  }, [state, load, adopt, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
